import React, { useMemo } from 'react';

import { Carousel, FaceSample } from '@vlabs/uikit';
import type { WithDataTestid } from '@vlabs/uikit/types';

import { useGetFaces } from '@vlabs/api/point-api/client';
import type { Face } from '@vlabs/api/point-api/model';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { PersonCardPin } from '../person-card-pin';
import st from './PersonCardFaces.module.sass';

type TPersonCardSize = 'large' | 'small';

interface PersonCardFacesProps extends WithDataTestid {
  cardId: string;
  size: TPersonCardSize;
}

const displayName = 'PersonCardFaces';
const testid = buildTestid(displayName);

function PersonCardFaces({
  cardId,
  size,
  'data-testid': dataTestId = undefined,
}: PersonCardFacesProps): React.JSX.Element {
  const getFaces = useGetFaces({ card_ids: [cardId], targets: ['avatar', 'is_card_face', 'face_id'] });

  const items: React.JSX.Element[] | undefined = useMemo(() => {
    if (getFaces.status !== 'success') return [];
    const faces = getFaces.data.faces ?? [];
    const cardFaceIndex = getFaces.data.faces?.findIndex?.(({ is_card_face }) => is_card_face === true);
    if (cardFaceIndex !== undefined && cardFaceIndex > -1) {
      const face = faces.splice(cardFaceIndex, 1)[0] as Face;
      faces.unshift(face);
    }
    return faces.map((face) => (
      <div
        className={st.PhotoContainer}
        key={face.face_id}
        {...testid(dataTestId)}
      >
        {!!face.is_card_face && size === 'large' && <PersonCardPin />}
        <FaceSample
          className={st.Sample}
          key={face.face_id}
          size={size}
          src={face.avatar ? `/api/lp5${face.avatar}` : undefined}
          {...testid(face.face_id)}
        />
      </div>
    ));
  }, [getFaces.data, getFaces.status, size, dataTestId]);

  if (!items) {
    return (
      <FaceSample
        className={st.PhotoContainer}
        key="empty"
        size={size}
        {...testid(dataTestId, 'empty')}
      />
    );
  }

  if (items.length === 1) {
    return <React.Fragment>{items[0]}</React.Fragment>;
  }

  return (
    <Carousel
      className={st.Carousel}
      items={items}
      {...testid(dataTestId)}
    />
  );
}

export { PersonCardFaces };
